import type { Feature, FeatureEstimate, RegistryEntry, FeatureRegistry } from "./schemas.js";

/**
 * Generate keywords from feature name, description, category, and AI tasks.
 */
function extractKeywords(feature: Feature): string[] {
  const words = new Set<string>();

  // Words from name
  for (const word of feature.name.toLowerCase().split(/\s+/)) {
    if (word.length > 2) words.add(word);
  }

  // Words from AI tasks
  for (const task of feature.aiTasks) {
    for (const word of task.toLowerCase().split(/[-_\s]+/)) {
      if (word.length > 2) words.add(word);
    }
  }

  // Category
  words.add(feature.category.toLowerCase());

  return Array.from(words);
}

/**
 * Generate file patterns from feature name and category.
 * These are best-guess patterns — refined by reconciliation.
 */
function guessFilePatterns(feature: Feature): string[] {
  const slug = feature.id;
  return [`src/**/*${slug}*`, `src/${slug}/**`];
}

function guessBranchPatterns(feature: Feature): string[] {
  return [`feature/${feature.id}*`, `feat/*${feature.id}*`];
}

export function generateRegistryEntry(
  feature: Feature,
  estimate: FeatureEstimate,
): RegistryEntry {
  return {
    id: feature.id,
    name: feature.name,
    keywords: extractKeywords(feature),
    file_patterns: guessFilePatterns(feature),
    git_branch_patterns: guessBranchPatterns(feature),
    estimated_tokens: estimate.withOverhead.total,
    status: "not-started",
  };
}

export function generateRegistry(
  project: string,
  features: Feature[],
  estimates: FeatureEstimate[],
): FeatureRegistry {
  const estimateMap = new Map(estimates.map((e) => [e.featureId, e]));

  return {
    project,
    features: features
      .map((f) => {
        const est = estimateMap.get(f.id);
        if (!est) return null;
        return generateRegistryEntry(f, est);
      })
      .filter((e): e is RegistryEntry => e !== null),
    generatedAt: new Date().toISOString(),
  };
}
